/**
 * whatsapp.ts — Helpers puros para construir URLs de WhatsApp (wa.me).
 *
 * QUÉ HACE:
 *   - Normaliza números (quita espacios, guiones, paréntesis, prefijos).
 *   - Valida si un número tiene formato compatible con WhatsApp.
 *   - Construye el URL `https://wa.me/NUMERO?text=MENSAJE` correctamente.
 *
 * QUÉ EXPORTA:
 *   - isValidWhatsappNumber(raw): boolean
 *   - buildWhatsappUrl(opts): string | null
 *   - buildWhatsappChatUrl(opts): string | null (solo destinatario, sin texto)
 *
 * QUÉ NO HACE:
 *   - No envía nada por sí solo. Solo construye URLs.
 *   - No depende de ningún backend ni librería externa.
 *   - No conoce de "recibos" ni de ningún módulo de negocio.
 *
 * REGLAS:
 *   - País por defecto: Colombia (57). Configurable por parámetro.
 *   - Si el número ya empieza con código de país (longitud > 10), se respeta.
 */

const DEFAULT_COUNTRY_CODE = "57"; // Colombia

/**
 * Quita todo lo que no sea dígito.
 * Si el resultado es un número local (10 dígitos en CO), antepone el código de país.
 */
function normalizeWhatsappNumber(
  raw: string | undefined | null,
  countryCode: string = DEFAULT_COUNTRY_CODE,
): string {
  if (!raw) return "";
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  // Si ya tiene código de país (más de 10 dígitos), usar tal cual
  if (digits.length > 10) return digits;
  // Si tiene exactamente 10 (móvil colombiano típico), anteponer 57
  if (digits.length === 10) return `${countryCode}${digits}`;
  // Otros casos (fijo local, número incompleto): devolver lo que haya
  return digits;
}

/**
 * Valida que un número sea apto para WhatsApp.
 * Requisito mínimo: al menos 10 dígitos después de normalizar.
 */
export function isValidWhatsappNumber(raw: string | undefined | null): boolean {
  const normalized = normalizeWhatsappNumber(raw);
  return normalized.length >= 10;
}

interface BuildWhatsappUrlOptions {
  /** Número del destinatario (puede venir con o sin código de país). */
  phone?: string | null;
  /** Texto pre-escrito para el mensaje. */
  message: string;
  /** Código de país por defecto si el número viene local. */
  countryCode?: string;
}

/**
 * Construye un URL `https://api.whatsapp.com/send?phone=NUMERO&text=MENSAJE`.
 * Usa api.whatsapp.com en lugar de wa.me — evita problemas de encoding con emojis.
 * Si no se pasa phone, omite el parámetro phone (WhatsApp pedirá destinatario).
 * Devuelve null si el mensaje está vacío.
 */
export function buildWhatsappUrl({
  phone,
  message,
  countryCode = DEFAULT_COUNTRY_CODE,
}: BuildWhatsappUrlOptions): string | null {
  if (!message || !message.trim()) return null;
  const encoded = encodeURIComponent(message);
  const normalized = phone ? normalizeWhatsappNumber(phone, countryCode) : "";
  if (normalized) {
    return `https://api.whatsapp.com/send?phone=${normalized}&text=${encoded}`;
  }
  return `https://api.whatsapp.com/send?text=${encoded}`;
}

/**
 * Construye un URL de chat de WhatsApp con SOLO el destinatario (sin texto).
 * Útil para abrir la conversación sin preescribir nada (ej. Botón de Pánico
 * en modo «Enviar alerta sin mensaje»). Devuelve null si no hay número válido.
 */
export function buildWhatsappChatUrl({
  phone,
  countryCode = DEFAULT_COUNTRY_CODE,
}: {
  phone?: string | null;
  countryCode?: string;
}): string | null {
  const normalized = phone ? normalizeWhatsappNumber(phone, countryCode) : "";
  if (!normalized) return null;
  return `https://api.whatsapp.com/send?phone=${normalized}`;
}
